import React, { useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CloudSun, Wind, Droplets, Sun } from "lucide-react";

export default function WeatherScheduler() {
  const [location, setLocation] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchWeather = async () => {
    if (!location.trim()) { toast.error("Enter a site location"); return; }
    setBusy(true);
    try { const r = await apiClient().get(`/weather/${encodeURIComponent(location)}`); setData(r.data); }
    catch { toast.error("Weather fetch failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 fade-in-up" data-testid="weather-page">
      <div>
        <h1 className="font-display text-5xl mt-1">Task Scheduler</h1>
      </div>

      <div className="border border-border bg-card p-5 flex gap-2">
        <input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="weather-location"
          className="flex-1 border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary" aria-label="Site location" />
        <button onClick={fetchWeather} disabled={busy} data-testid="weather-fetch-btn"
          className="btn-sharp bg-primary text-primary-foreground px-4 py-2 text-sm font-medium disabled:opacity-50">
          {busy ? "…" : "Fetch"}
        </button>
      </div>

      {data && (
        <>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="border border-border bg-card p-5">
              <CloudSun className="h-5 w-5 text-primary" />
              <div className="font-display text-3xl mt-2">{data.temperature}°C</div>
              <div className="mono text-xs text-muted-foreground mt-1">{data.condition}</div>
            </div>
            <div className="border border-border bg-card p-5">
              <Wind className="h-5 w-5 text-primary" />
              <div className="font-display text-3xl mt-2">{data.wind_kmh}</div>
              <div className="mono text-xs text-muted-foreground mt-1">km/h wind</div>
            </div>
            <div className="border border-border bg-card p-5">
              <Droplets className="h-5 w-5 text-primary" />
              <div className="font-display text-3xl mt-2">{data.humidity}%</div>
              <div className="mono text-xs text-muted-foreground mt-1">humidity</div>
            </div>
            <div className="border border-border bg-card p-5">
              <Sun className="h-5 w-5 text-primary" />
              <div className={`font-display text-3xl mt-2 ${data.safe_for_work ? "text-emerald-500" : "text-destructive"}`}>
                {data.safe_for_work ? "GO" : "HALT"}
              </div>
              <div className="mono text-xs text-muted-foreground mt-1">work status</div>
            </div>
          </div>

          <div className="border border-border bg-card p-5">
            <div className="font-heading text-lg mb-3">7-Day Site Workability</div>
            <div className="grid grid-cols-7 gap-2">
              {data.forecast_7day.map((d) => (
                <div key={d.day} className={`border p-3 text-center ${d.workable ? "border-border" : "border-destructive/40 bg-destructive/5"}`} data-testid={`fc-${d.day}`}>
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{d.day}</div>
                  <div className="font-display text-2xl mt-1">{d.temp}°</div>
                  <div className="mono text-[10px] text-muted-foreground truncate">{d.condition}</div>
                  <div className={`mono text-[10px] uppercase mt-2 ${d.workable ? "text-emerald-500" : "text-destructive"}`}>
                    {d.workable ? "WORK" : "STOP"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
